<script>
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import { MODAL, ACTION } from '@constants/Text'
import { ICONS } from '@modules/atendimento/constants/Text'
import apiAtendimento from '@modules/atendimento/api/apiAtendimento'
import ModalRespostaRapida from '@modules/atendimento/modal/modal-resposta-rapida/modal-resposta-rapida.vue'

export default {
  name: 'ModalGerenciarRespostas',
  components: { ModalRespostaRapida },
  props: {
    // API + namespace + chaves GlobalBus — default atendimento.
    // Solicitacoes injeta as suas; respostaRapidaKey também é repassada ao
    // ModalRespostaRapida filho (pra ele escutar a chave certa).
    api: {
      type: Object,
      default: () => apiAtendimento,
    },
    namespace: {
      type: String,
      default: 'atendimento',
    },
    openKey: {
      type: String,
      default: MODAL.ATENDIMENTO_GERENCIAR_RESPOSTAS,
    },
    salvaKey: {
      type: String,
      default: ACTION.ATENDIMENTO_RESPOSTA_RAPIDA_SALVA,
    },
    respostaRapidaKey: {
      type: String,
      default: MODAL.ATENDIMENTO_RESPOSTA_RAPIDA,
    },
  },
  data() {
    return {
      icons: ICONS,
      busModal: new Vue(),
      loading: false,
      respostas: [],
    }
  },
  computed: {
    tableHeaders() {
      return [
        { text: 'Título', value: 'titulo', sortable: true, width: '30%' },
        { text: 'Conteúdo', value: 'conteudo', sortable: false },
        {
          text: '',
          value: 'actions',
          align: 'center',
          sortable: false,
          width: '100px',
        },
      ]
    },
  },
  created() {
    GlobalBus.$on(this.openKey, this.open)
    GlobalBus.$on(this.salvaKey, this.onRespostaSalva)
  },
  beforeDestroy() {
    GlobalBus.$off(this.openKey, this.open)
    GlobalBus.$off(this.salvaKey, this.onRespostaSalva)
  },
  methods: {
    clear() {
      this.loading = false
      this.respostas = []
    },

    open() {
      this.clear()
      this.busModal.$emit('dialog', true)
      this.fetchRespostas()
    },

    async fetchRespostas() {
      this.loading = true
      try {
        const r = await this.api.listarRespostasRapidas()
        if (r && r.status === 200) {
          this.respostas = (r.value || []).map((item) => ({
            ...item,
            loading_delete: false,
          }))
        }
      } finally {
        this.loading = false
      }
    },

    openNewForm() {
      GlobalBus.$emit(this.respostaRapidaKey)
    },

    openEditForm(item) {
      GlobalBus.$emit(this.respostaRapidaKey, { item })
    },

    onRespostaSalva() {
      this.fetchRespostas()
      this.$store.dispatch(`${this.namespace}/listarRespostasRapidas`)
    },

    onDeleteClick(item) {
      if (item.loading_delete) return
      this.$showModal({
        title: 'Remover resposta rápida',
        text: 'Deseja remover esta resposta rápida? Essa ação não pode ser desfeita.',
        type: 'danger',
        v2: true,
        center: true,
        buttons: [
          { text: 'Cancelar' },
          {
            text: 'Remover',
            action: () => {
              this.deleteResposta(item)
            },
          },
        ],
      })
    },

    async deleteResposta(item) {
      if (item.loading_delete) return
      item.loading_delete = true
      try {
        const r = await this.api.deletarRespostaRapida({ id: item.id })
        this.$showSnack({
          text: (r && r.message) || 'Erro inesperado',
          color: r && r.status === 200 ? 'success' : 'error',
          button: true,
        })
        if (r && r.status === 200) {
          this.fetchRespostas()
          this.$store.dispatch(`${this.namespace}/listarRespostasRapidas`)
        }
      } finally {
        item.loading_delete = false
      }
    },
  },
}
</script>

<template>
  <div>
    <BaseModal
      centerScreen
      :bus="busModal"
      persistent
      centerAndDecreasesPadding
      maxWidth="55%"
      closeColor="#8e8e8e"
      backgroundColor="white"
    >
      <div class="pb-6">
        <div class="modal-title-container">
          <span class="modal-title">Gerenciar Respostas Rápidas</span>
        </div>

        <div class="modal-content">
          <div class="pa-4">
            <!-- BaseTable com search + cornerAction (Nova Resposta) -->
            <BaseTable
              :headers="tableHeaders"
              :items="respostas"
              :loading="loading"
              show-search
              disable-margin-top
              hide-default-footer
              :items-per-page="-1"
              :no-data-text="'Nenhuma resposta rápida cadastrada'"
              :no-results-text="'Nenhuma resposta encontrada'"
              data-test="respostas-table"
            >
              <template v-slot:cornerAction>
                <BaseButton
                  primary
                  fit-content
                  min-width="auto"
                  data-test="add-resposta-btn"
                  @click="openNewForm"
                >
                  Nova Resposta
                </BaseButton>
              </template>

              <template v-slot:item.titulo="{ item }">
                <span>
                  {{ item.titulo }}
                </span>
              </template>

              <template v-slot:item.conteudo="{ item }">
                <span
                  class="d-inline-block text-truncate"
                  style="max-width: 320px"
                  :title="item.conteudo"
                >
                  {{ item.conteudo }}
                </span>
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        data-test="edit-resposta-btn"
                        v-bind="attrs"
                        v-on="on"
                        @click="openEditForm(item)"
                      >
                        <icon-svg
                          :name="icons.edit"
                          size="16"
                          color="#8E8E8E"
                        />
                      </v-btn>
                    </template>
                    <span>Editar</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="d-flex" v-on="on">
                        <v-btn
                          icon
                          small
                          data-test="delete-resposta-btn"
                          :disabled="item.loading_delete"
                          v-bind="attrs"
                          @click="onDeleteClick(item)"
                        >
                          <v-progress-circular
                            v-if="item.loading_delete"
                            indeterminate
                            size="16"
                            width="2"
                            color="grey"
                          />
                          <icon-svg
                            v-else
                            :name="icons.delete"
                            size="16"
                            color="#8E8E8E"
                          />
                        </v-btn>
                      </div>
                    </template>
                    <span>Remover</span>
                  </v-tooltip>
                </div>
              </template>
            </BaseTable>
          </div>
        </div>
      </div>

      <template v-slot:actions="{ close }">
        <BaseButton underlined @click="close"> Fechar </BaseButton>
      </template>
    </BaseModal>

    <ModalRespostaRapida
      :api="api"
      :open-key="respostaRapidaKey"
      :salva-key="salvaKey"
    />
  </div>
</template>
